import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import useModal, { useCloseAllModal } from 'hooks/useModal'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'state'
import { useAllWageringBonusesHaveAmount } from 'state/bonus/hooks'
import styled from 'styled-components'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import { ModalProps } from 'UIKit/ModalV2'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { getBalanceTokenKey } from 'utils'
import { PaymentPageEnums } from 'views/Payment/Menu'
import { PaymentHistoryButton } from '../components/PaymentHistoryModal'
import PaymentTokenSelectModal from '../components/PaymentTokenSelectModal'
import { PAYMENT_MODAL_THEME, StylePaymentModal } from '../components/styles'
import Swap from '../Swap'
import WithdrawForm from './WithdrawForm'

const WithdrawFormModal: React.FC<
  ModalProps<{
    selectedToken: Token
    onSelect: (token: Token) => void
  }>
> = ({ onDismiss, data: { onSelect, selectedToken } }) => {
  const bonuses = useAllWageringBonusesHaveAmount()
  const closeAll = useCloseAllModal()
  const [onPresent] = useModal(Swap)
  const onBack = useFullSizeModalRouteUpdater('Withdraw', onDismiss)
  const { t } = useTranslation()

  const handleClose = () => {
    onSelect(null)
    onBack()
  }

  return (
    <CustomModal
      sizeMode={ModalSizeModeEnum.Full}
      colorTheme={PAYMENT_MODAL_THEME}
      disableClose
      id="payment"
      onDismiss={handleClose}
    >
      <ModalHeader
        useBackIcon
        hideClose
        hideStroke
        colorTheme={PAYMENT_MODAL_THEME}
        titleAlign="center"
        onBack={handleClose}
        title={
          <Text
            width="fit-content"
            display="inline-flex"
            style={{
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            fontWeight={600}
            color="currentColor"
          >
            <TokenLogo token={selectedToken} hideNetwork size={20} />
            {t('Withdraw')} {selectedToken.name}
          </Text>
        }
        suffixNode={<PaymentHistoryButton from={PaymentPageEnums.Withdraw} />}
      />

      <ModalBody overflowY="scroll" height="100%" gap="20px">
        <WithdrawForm
          selectedToken={selectedToken}
          onBack={handleClose}
          onClose={closeAll}
          bonusControl={{
            bonuses,
          }}
          onRedirectToSwap={onPresent}
        />
      </ModalBody>
    </CustomModal>
  )
}

const Withdraw: React.FC<
  ModalProps<{
    initialToken?: Token
    isFiatCurrency?: boolean
  }>
> = ({ onDismiss }) => {
  const [selectedToken, setSelectedToken] = useState<Token>()
  const tokenBalances = useAppSelector((state) => state.profile.balances)
  const [onPresent] = useModal(WithdrawFormModal)

  const selectToken = useCallback((token: Token) => {
    setSelectedToken(token)
    if (!token) return
    onPresent({
      onSelect: setSelectedToken,
      selectedToken: token,
    })
  }, [])

  return (
    <PaymentTokenSelectModal
      data={{
        fetchBalance: (token) => {
          return Promise.resolve(new BigNumber(tokenBalances[getBalanceTokenKey(token.network, token.code)] || 0))
        },
        selectedToken,
        onCurrencySelect: selectToken,
        isIncludeFiat: true,
        isIncludeAll: true,
        hideNetwork: false,
        from: PaymentPageEnums.Withdraw,
      }}
      onDismiss={onDismiss}
    />
  )
}

const CustomModal = styled(StylePaymentModal)`
  .wrap-action-header {
    max-height: 21px;
  }
`

export default Withdraw
